import { Quaternion as BABYLON_Quaternion, Vector2 as BABYLON_Vector2, Vector3 as BABYLON_Vector3, Vector4 as BABYLON_Vector4, Matrix as BABYLON_Matrix } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
import { Plane as BABYLON_Plane } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.plane.js';
export const DEG2RAD = Math.PI / 180.0;
export const RAD2DEG = 180.0 / Math.PI;
export const EPSILON_DISTANCE = 0.0001; // This is 10th of a milimeter, which at this time seems like a small enough value.
export const EPSILON_DISTANCE_SQUARED = EPSILON_DISTANCE * EPSILON_DISTANCE;
export const EPSILON_ANGLE_DEGREES = 0.05;
export const EPSILON_ANGLE_RADIANS = EPSILON_ANGLE_DEGREES * DEG2RAD;
export const PARALLEL_DOT = Math.cos(EPSILON_ANGLE_RADIANS); // The precision of this value is subject to change and has been chosen based on observed bahaviour.
export { BABYLON_Vector2 as Vector2 };
export { BABYLON_Vector3 as Vector3 };
export { BABYLON_Vector4 as Vector4 };
export { BABYLON_Quaternion as Quaternion };
export { BABYLON_Matrix as Matrix };
export { BABYLON_Plane as Plane };
class ObjectPool {
    constructor(CONSTRUCTOR, ...args) {
        this.CONSTRUCTOR = CONSTRUCTOR;
        this.pool_storage = [];
        this.pool_size = 100;
        this.pool_storage.length = this.pool_size;
        this._args = args;
        for (let i = 0; i < this.pool_size; i++) {
            this.pool_storage[i] = new this.CONSTRUCTOR(...args);
        }
    }
    fetch() {
        if (this.pool_storage.length === 0) {
            console.warn(`Object pool for ${this.CONSTRUCTOR.name} ran out of objects with a size of ${this.pool_size}`);
            return new this.CONSTRUCTOR(...this._args);
        }
        else {
            // return last object in pool
            return this.pool_storage.pop();
        }
    }
    return(...val) {
        if (this.pool_storage.length > this.pool_size) {
            console.warn(`Object pool for ${this.CONSTRUCTOR.name} has over ${this.pool_size} objects, dropping objects`);
        }
        else {
            for (let i = 0; i < val.length; i++) {
                this.pool_storage.push(val[i]);
            }
        }
    }
}
export const vector2_pool = new ObjectPool(BABYLON_Vector2);
export const vector3_pool = new ObjectPool(BABYLON_Vector3);
export const vector4_pool = new ObjectPool(BABYLON_Vector4);
export const quaternion_pool = new ObjectPool(BABYLON_Quaternion);
export const matrix_pool = new ObjectPool(BABYLON_Matrix);
export function determinant3x3(m) {
    if (m.length !== 16)
        throw new Error('determinant3x3 expects an array of length 16 (4x4)');
    const detA = (m[4 * 1 + 1] * m[4 * 2 + 2] - m[4 * 1 + 2] * m[4 * 2 + 1]);
    const detB = (m[4 * 1 + 2] * m[4 * 2 + 0] - m[4 * 1 + 0] * m[4 * 2 + 2]);
    const detC = (m[4 * 1 + 0] * m[4 * 2 + 1] - m[4 * 1 + 1] * m[4 * 2 + 0]);
    return detA * m[4 * 0 + 0] + detB * m[4 * 0 + 1] + detC * m[4 * 0 + 2];
}
export function imatrix4_equals_babylon_matrix(imatrix, bmatrix) {
    // Because babylon actually uses a Float32Array as their backing storage for their matrices, we need to compare in their float space.
    // So we convert the viewer matrix to a babylon matrix first, and then compare that with the given babylon matrix.
    const temporary_compare_babylon_matrix = matrix_pool.fetch().fromIMatrix4(imatrix);
    const result = temporary_compare_babylon_matrix.equals(bmatrix);
    matrix_pool.return(temporary_compare_babylon_matrix);
    return result;
}
export function horizontal_to_vertical_fov(horizontal_fov, aspect_ratio) {
    return 2 * Math.atan(Math.tan(horizontal_fov * 0.5) / aspect_ratio);
}
export function vertical_to_horizontal_fov(vertical_fov, aspect_ratio) {
    return 2 * Math.atan(Math.tan(vertical_fov * 0.5) * aspect_ratio);
}
export function clamp(a, min, max) {
    return Math.min(Math.max(a, min), max);
}
export function lerp(a, b, scale) {
    return a * (1 - scale) + b * scale;
}
/**
 * Returns the solid angle of an angle.
 * @param a an angle measured in radians
 * @returns solid angle measured in steradians
 */
export function solid_angle(a) {
    // 2*pi*(1-cos(a/2))
    return 2 * Math.PI * (1 - Math.cos(a / 2));
}
/**
 * Rotate a vector.
 */
export function rotate(vector3, delta_angle, rotation_axis) {
    // TODO: Fix new once merged to main
    const quaternion = new BABYLON_Quaternion();
    BABYLON_Quaternion.RotationAxisToRef(new BABYLON_Vector3(rotation_axis.x, rotation_axis.y, rotation_axis.z), delta_angle, quaternion);
    const babylon_vector = new BABYLON_Vector3(vector3.x, vector3.y, vector3.z)
        .rotateByQuaternionToRef(quaternion, new BABYLON_Vector3(0.0, 0.0, 0.0));
    return { x: babylon_vector.x, y: babylon_vector.y, z: babylon_vector.z };
}
export function radians(degrees) {
    return Math.PI * degrees / 180;
}
export function degrees(radians) {
    return 180 * radians / Math.PI;
}
export class TrackedVec3 {
    constructor(x = 0.0, y = 0.0, z = 0.0) {
        this._x = 0;
        this._y = 0;
        this._z = 0;
        this._x = x;
        this._y = y;
        this._z = z;
    }
    report_change() {
        if (this.onchange) {
            this.onchange();
        }
    }
    get x() {
        return this._x;
    }
    set x(v) {
        if (this._x === v)
            return;
        this._x = v;
        this.report_change();
    }
    get y() {
        return this._y;
    }
    set y(v) {
        if (this._y === v)
            return;
        this._y = v;
        this.report_change();
    }
    get z() {
        return this._z;
    }
    set z(v) {
        if (this._z === v)
            return;
        this._z = v;
        this.report_change();
    }
    get r() {
        return this._x;
    }
    set r(v) {
        if (this._x === v)
            return;
        this._x = v;
        this.report_change();
    }
    get g() {
        return this._y;
    }
    set g(v) {
        if (this._y === v)
            return;
        this._y = v;
        this.report_change();
    }
    get b() {
        return this._z;
    }
    set b(v) {
        if (this._z === v)
            return;
        this._z = v;
        this.report_change();
    }
    set(x, y, z) {
        if (this._x !== x || this._y !== y || this._z !== z) {
            this._x = x;
            this._y = y;
            this._z = z;
            this.report_change();
        }
    }
    copy(v) {
        this.set(v.x, v.y, v.z);
    }
}
export function get_euler_angles_from_rotation_matrix(transform) {
    const matrix = matrix_pool.fetch().fromIMatrix4(transform);
    const rotation_matrix = matrix.getRotationMatrixToRef(matrix_pool.fetch());
    const quaternion = new BABYLON_Quaternion();
    quaternion.fromRotationMatrix(rotation_matrix);
    matrix_pool.return(matrix, rotation_matrix);
    return quaternion.toEulerAngles();
}
//# sourceMappingURL=Math.js.map